import { useState } from 'react';
import {
  Plus,
  X,
  Bell,
  FileOutput,
  RefreshCw,
  Truck,
  Mail,
  History,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ApprovalAction, ApprovalActionType } from '@/api/services';

interface ApprovalActionsConfigProps {
  approvalActions: ApprovalAction[];
  rejectionActions: ApprovalAction[];
  onApprovalActionsChange: (actions: ApprovalAction[]) => void;
  onRejectionActionsChange: (actions: ApprovalAction[]) => void;
}

const actionTypeConfig: Record<
  ApprovalActionType,
  {
    label: string;
    icon: React.ReactNode;
    description: string;
    forApproval: boolean;
    forRejection: boolean;
  }
> = {
  PublishToRateEngine: {
    label: 'Publish to Rate Engine',
    icon: <FileOutput className="w-4 h-4" />,
    description: 'Automatically publish awarded rate to rate engine',
    forApproval: true,
    forRejection: false,
  },
  SendNotification: {
    label: 'Send Notification',
    icon: <Bell className="w-4 h-4" />,
    description: 'Send email/system notification',
    forApproval: true,
    forRejection: true,
  },
  UpdateRFQStatus: {
    label: 'Update RFQ Status',
    icon: <RefreshCw className="w-4 h-4" />,
    description: 'Change the RFQ status automatically',
    forApproval: true,
    forRejection: true,
  },
  CreateShipment: {
    label: 'Create Shipment',
    icon: <Truck className="w-4 h-4" />,
    description: 'Automatically create shipment from award',
    forApproval: true,
    forRejection: false,
  },
  ResetAwardStatus: {
    label: 'Reset Award Status',
    icon: <RefreshCw className="w-4 h-4" />,
    description: 'Reset the award status back to pending',
    forApproval: false,
    forRejection: true,
  },
  NotifyCarrier: {
    label: 'Notify Carrier',
    icon: <Mail className="w-4 h-4" />,
    description: 'Send notification to awarded carrier',
    forApproval: true,
    forRejection: true,
  },
  LogEvent: {
    label: 'Log Event',
    icon: <History className="w-4 h-4" />,
    description: 'Log this event to audit trail',
    forApproval: true,
    forRejection: true,
  },
};

const rfqStatuses = ['Draft', 'Open', 'Closed', 'Awarded', 'Cancelled'];

export function ApprovalActionsConfig({
  approvalActions,
  rejectionActions,
  onApprovalActionsChange,
  onRejectionActionsChange,
}: ApprovalActionsConfigProps) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Approval Actions */}
      <Card>
        <CardHeader className="pb-3">
          <h3 className="text-base font-semibold text-foreground">
            <CheckCircle className="w-5 h-5 text-status-success-foreground" />
            On Approval Actions
          </h3>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Actions to execute when approval is complete
          </p>
          <ActionList
            actions={approvalActions}
            onChange={onApprovalActionsChange}
            forApproval={true}
          />
        </CardContent>
      </Card>

      {/* Rejection Actions */}
      <Card>
        <CardHeader className="pb-3">
          <h3 className="text-base font-semibold text-foreground">
            <XCircle className="w-5 h-5 text-status-destructive-foreground" />
            On Rejection Actions
          </h3>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Actions to execute when approval is rejected
          </p>
          <ActionList
            actions={rejectionActions}
            onChange={onRejectionActionsChange}
            forApproval={false}
          />
        </CardContent>
      </Card>
    </div>
  );
}

interface ActionListProps {
  actions: ApprovalAction[];
  onChange: (actions: ApprovalAction[]) => void;
  forApproval: boolean;
}

function ActionList({ actions, onChange, forApproval }: ActionListProps) {
  const [showAddAction, setShowAddAction] = useState(false);

  const availableTypes = (Object.keys(actionTypeConfig) as ApprovalActionType[]).filter((type) => {
    const config = actionTypeConfig[type];
    return forApproval ? config.forApproval : config.forRejection;
  });

  const handleAddAction = (type: ApprovalActionType) => {
    const newAction: ApprovalAction = {
      type,
      metadata: {},
    };

    // Set defaults based on type
    if (type === 'UpdateRFQStatus') {
      newAction.status = forApproval ? 'Awarded' : 'Open';
    }

    onChange([...actions, newAction]);
    setShowAddAction(false);
  };

  const handleUpdateAction = (index: number, updates: Partial<ApprovalAction>) => {
    const updated = [...actions];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const handleRemoveAction = (index: number) => {
    onChange(actions.filter((_, i) => i !== index));
  };

  const handleToggleAction = (index: number) => {
    const updated = [...actions];
    updated[index] = {
      ...updated[index],
      metadata: {
        ...updated[index].metadata,
        disabled: !updated[index].metadata?.disabled,
      },
    };
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {actions.map((action, index) => {
        const config = actionTypeConfig[action.type];
        const isDisabled = action.metadata?.disabled;

        return (
          <div
            key={index}
            className={`flex items-start gap-3 p-3 rounded-lg border ${
              isDisabled ? 'bg-muted/50 opacity-60' : 'bg-card'
            }`}
          >
            <div className="flex-shrink-0 mt-0.5">{config.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm">{config.label}</span>
                {isDisabled && (
                  <Badge variant="outline" className="text-xs">
                    Disabled
                  </Badge>
                )}
              </div>

              {/* Action-specific config */}
              {action.type === 'UpdateRFQStatus' && (
                <div className="flex items-center gap-2 mt-2">
                  <Label className="text-xs">Set status to:</Label>
                  <Select
                    value={action.status || ''}
                    onValueChange={(value) => handleUpdateAction(index, { status: value })}
                  >
                    <SelectTrigger className="h-8 w-32">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {rfqStatuses.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {action.type === 'SendNotification' && (
                <div className="mt-2">
                  <Label className="text-xs">Template (optional):</Label>
                  <Input
                    value={action.template || ''}
                    onChange={(e) => handleUpdateAction(index, { template: e.target.value })}
                    placeholder="notification-template-id"
                    className="h-8 mt-1"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center gap-1">
              <Switch checked={!isDisabled} onCheckedChange={() => handleToggleAction(index)} />
              <Button
                variant="ghost"
                size="icon"
                aria-label="Dismiss"
                className="h-8 w-8"
                onClick={() => handleRemoveAction(index)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        );
      })}

      {showAddAction ? (
        <div className="border rounded-lg p-3 border-dashed">
          <Label className="text-sm mb-2 block">Select Action Type</Label>
          <div className="grid gap-2">
            {availableTypes.map((type) => {
              const config = actionTypeConfig[type];
              const isUsed = actions.some((a) => a.type === type);
              return (
                <Button
                  key={type}
                  variant="outline"
                  className="justify-start h-auto py-2"
                  disabled={isUsed}
                  onClick={() => handleAddAction(type)}
                >
                  <div className="flex items-center gap-3">
                    {config.icon}
                    <div className="text-left">
                      <div className="font-medium text-sm">{config.label}</div>
                      <div className="text-xs text-muted-foreground">{config.description}</div>
                    </div>
                  </div>
                </Button>
              );
            })}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => setShowAddAction(false)}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="border-dashed w-full"
          onClick={() => setShowAddAction(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Action
        </Button>
      )}
    </div>
  );
}
